// TrialEndedSheet.tsx — the ONE-TIME "Your free trial has ended" sheet (TRIAL_FULL_LOCK).
// Shown the first time a lapsed non-member reaches the app past onboarding/consent; the seen
// flag (progress.trialEndedSeenAt) is persisted the moment it appears, so it shows once per
// install. Never for members, during the trial, or with activation off (featuresLocked is
// already false in all of those). Waits while a bigger overlay (crisis sheet etc.) is up.
// House style: no emoji, no em-dash.
import React from 'react';
import { Btn } from '@/components/ui';
import { F, SIZE } from '@/new/theme';
import { useAuth } from '@/hooks/useAuth';
import { useMembershipRecheckOnResume } from '@/hooks/useMembershipRecheckOnResume';
import { useProgress } from '@/hooks/useProgress';
import { TRIAL_FULL_LOCK } from '@/config/features';

export const TRIAL_ENDED_BODY =
  'Thank you for trying ReNurture. To keep going with the Recovery programme, the Toolkit, the Library, calming tools and Guide me, become a member. Feeling unsafe support and the Self-compassion course stay free for you.';

export function shouldShowTrialEnded(o: { fullLock: boolean; seenAt: string | null | undefined; blocked: boolean }): boolean {
  return o.fullLock && !o.seenAt && !o.blocked;
}

export function TrialEndedSheet({ blocked, onSeeMembership }: { blocked: boolean; onSeeMembership: () => void }) {
  const auth = useAuth();
  const progress = useProgress();
  const [open, setOpen] = React.useState(false);
  const fullLock = TRIAL_FULL_LOCK && !!auth.featuresLocked;
  const eligible = shouldShowTrialEnded({ fullLock, seenAt: progress.state.trialEndedSeenAt, blocked });

  React.useEffect(() => {
    if (!eligible) return;
    setOpen(true);
    progress.markTrialEndedSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible]);

  // Becoming a member (or any change that lifts the lock) closes it.
  React.useEffect(() => { if (!fullLock) setOpen(false); }, [fullLock]);

  useMembershipRecheckOnResume(open, () => setOpen(false));

  if (!open) return null;
  const close = () => setOpen(false);
  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="trial-ended-title" style={panel}>
        <div id="trial-ended-title" style={{ fontFamily: F.title, fontSize: 22, fontWeight: 500, color: 'var(--ink)', marginBottom: 8 }}>
          Your free trial has ended
        </div>
        <p style={{ color: 'var(--ink-soft)', fontSize: SIZE.body, lineHeight: 1.55, margin: '0 0 18px', fontFamily: 'Inter, system-ui, sans-serif' }}>
          {TRIAL_ENDED_BODY}
        </p>
        <Btn full size="lg" onClick={() => { close(); onSeeMembership(); }}>See membership</Btn>
        <button type="button" onClick={close} style={quietBtn}>Not now</button>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 1100,
  background: 'rgba(20,16,12,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  paddingTop: 'calc(var(--safe-top) + 24px)',
  paddingBottom: 'calc(var(--safe-bottom) + 24px)',
};
const panel: React.CSSProperties = {
  width: '100%',
  maxWidth: 400,
  background: 'var(--card-top, #fff)',
  border: '1px solid var(--hair-strong, rgba(0,0,0,0.1))',
  borderRadius: 20,
  padding: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
};
const quietBtn: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 12,
  padding: '10px',
  border: 'none',
  background: 'none',
  color: 'var(--ink-soft)',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};
